// These are the helper methods that requires converting the string into localized Content

import { WorkspaceCategoryId, WorkspaceId } from '../../Common/Constants';
import { AppSessionContext } from '../../Services/Services.Primary/SessionContext/AppSessionContext';
import LocalizationService from '../../Services/Services.Secondary/Localization/LocalizationService';
import { isEmptyOrSpaces } from './DataValidity.Helper';

let appSessionContext: AppSessionContext | undefined;
const resx = LocalizationService.getInstance();
let locale: string | undefined;

const MinuteAgoText = resx.getLocalizedResource('MinuteAgo_text');
const MinutesAgoText = resx.getLocalizedResource('MinutesAgo_text');
const HourAgoText = resx.getLocalizedResource('HourAgo_text');
const HoursAgoText = resx.getLocalizedResource('HoursAgo_text');
const DayAgoText = resx.getLocalizedResource('DayAgo_text');
const DaysAgoText = resx.getLocalizedResource('DaysAgo_text');
const HelpAndSupport = resx.getLocalizedResource('HelpSupport_text');
const Benefits = resx.getLocalizedResource('BenefitsWorkspace');
const Billing = resx.getLocalizedResource('BillingWorkspace');
const Collaborate = resx.getLocalizedResource('CollaborateWorkspace');
const Customers = resx.getLocalizedResource('CustomersWorkspace');
const Insights = resx.getLocalizedResource('InsightsWorkspace');
const Membership = resx.getLocalizedResource('MembershipWorkspace');
const MarketplaceOffers = resx.getLocalizedResource('MarketplaceOffersWorkspace');
const Referrals = resx.getLocalizedResource('ReferralsWorkspace');
const Earnings = resx.getLocalizedResource('EarningsWorkspace');
const Pricing = resx.getLocalizedResource('PricingWorkspace');
const ActionCenter = resx.getLocalizedResource('ActionCenterWorkspace');
const AppsAndGames = resx.getLocalizedResource('AppsAndGamesWorkspace');
const Claims = resx.getLocalizedResource('ClaimsWorkspace');
const CustomerResearch = resx.getLocalizedResource('CustomerResearchWorkspace');
const Edge = resx.getLocalizedResource('EdgeWorkspace');
const Hardware = resx.getLocalizedResource('HardwareWorkspace');
const Incentives = resx.getLocalizedResource('IncentivesWorkspace');
const Quotes = resx.getLocalizedResource('QuotesWorkspace');
const QuotesHelp = resx.getLocalizedResource('QuotesHelpWorkspace');
const AccountSettings = resx.getLocalizedResource('AccountSettingsWorkspace');
const ApiAndIntegrationSupport = resx.getLocalizedResource('ApiAndIntegrationSupportWorkspace');
const Enrollment = resx.getLocalizedResource('EnrollmentWorkspace');
const DeveloperTools = resx.getLocalizedResource('DeveloperToolsWorkspace');
const MicrosoftDynamics = resx.getLocalizedResource('MicrosoftDynamicsWorkspace');
const ProductSupport = resx.getLocalizedResource('ProductSupportWorkspace');
const VolumeLicensing = resx.getLocalizedResource('VolumeLicensingWorkspace');

const updateLocale = () => {
  if (!appSessionContext) {
    appSessionContext = AppSessionContext.getInstance();
    locale = appSessionContext.Locale;
  }
};

/**
 * Converts data string to locale Format.
 * @param date string
 * @param locale current session locales
 * @returns
 */
export const displayLocalDate = (date: string | undefined | null, localeOverride?: string): string => {
  updateLocale();

  if (date && !isEmptyOrSpaces(date)) {
    return new Date(date).toLocaleDateString(localeOverride ?? locale);
  } else {
    return '';
  }
};

export const displayTimeSince = (date: string | undefined | null, localeOverride?: string) => {
  updateLocale();

  if (date) {
    const givenDate = new Date(date);
    const current = new Date();

    const minutesSince = (new Date(current.toUTCString()).getTime() - givenDate.getTime()) / (1000 * 60);

    if (minutesSince < 1) {
      return 'Now';
    }

    //TODO: make resources for these strings
    if (minutesSince < 60) {
      const minutes = Math.floor(minutesSince).toString();
      const minuteString = minutes === '1' ? ' ' + MinuteAgoText : ' ' + MinutesAgoText;
      return minutes + minuteString;
    }
    const hoursSince = minutesSince / 60;
    if (hoursSince < 24) {
      const hours = Math.floor(hoursSince).toString();
      const hourString = hours === '1' ? ' ' + HourAgoText : ' ' + HoursAgoText;
      return hours + hourString;
    }

    const daysSince = hoursSince / 24;
    if (daysSince < 31) {
      const days = Math.floor(daysSince).toString();
      const dayString = days === '1' ? ' ' + DayAgoText : ' ' + DaysAgoText;
      return days + dayString;
    } else {
      return givenDate.toLocaleDateString(localeOverride ?? locale);
    }
  } else {
    return '';
  }
};

export const getWorkspaceTagFromId = (workspaceId: string) => {
  updateLocale();

  switch (workspaceId) {
    case WorkspaceCategoryId.ActionCenterId:
      return ActionCenter;
    case WorkspaceId.AppsAndGamesId:
      return AppsAndGames;
    case WorkspaceId.BenefitsId:
      return Benefits;
    case WorkspaceId.BillingId:
      return Billing;
    case WorkspaceId.ClaimsId:
      return Claims;
    case WorkspaceId.CollaborateId:
      return Collaborate;
    case WorkspaceId.CustomersId:
      return Customers;
    case WorkspaceId.CustomerResearchId:
      return CustomerResearch;
    case WorkspaceId.EdgeId:
      return Edge;
    case WorkspaceId.HardwareId:
      return Hardware;
    case WorkspaceId.IncentivesId:
      return Incentives;
    case WorkspaceId.InsightsId:
      return Insights;
    case WorkspaceId.MembershipId:
      return Membership;
    case WorkspaceId.MarketplaceOffersId:
      return MarketplaceOffers;
    case WorkspaceId.ReferralsId:
      return Referrals;
    case WorkspaceId.EarningsId:
      return Earnings;
    case WorkspaceId.PricingId:
      return Pricing;
    case WorkspaceId.QuotesId:
      return Quotes;
    case WorkspaceId.QuotesHelpId:
      return QuotesHelp;
    case WorkspaceId.HelpAndSupportId:
      return HelpAndSupport;
    case WorkspaceCategoryId.AccountSettingsId:
      return AccountSettings;
    case WorkspaceCategoryId.ApiAndIntegrationSupportId:
    case WorkspaceCategoryId.ApiAndIntegrationSupportOldId:
      return ApiAndIntegrationSupport;
    case WorkspaceCategoryId.EnrollmentId:
    case WorkspaceCategoryId.EnrollmentOldId:
      return Enrollment;
    case WorkspaceCategoryId.DeveloperTools:
      return DeveloperTools;
    case WorkspaceCategoryId.MicrosoftDynamics:
      return MicrosoftDynamics;
    case WorkspaceCategoryId.ProductSupport:
      return ProductSupport;
    case WorkspaceCategoryId.VolumeLicensing:
      return VolumeLicensing;
    default:
      return '';
  }
};
